/**
 * A judgment row on the rail (plan 2D; Leo 10-31 C1-C4, C9, B6): its steps and its price lines. Read only when the
 * feed says judgment_owed; the codec drops `item.judgmentOwed` otherwise, so OFF = HEAD.
 */
import {
  FDNY_REVIEW_DAYS,
  JUDGMENT_CLOCK_WORD,
  JUDGMENT_STEP_FDNY_CERT,
  JUDGMENT_STEP_PAY,
  amnestyOpen,
  certAgency,
  type ActionItem,
  type ActionPlanFeed,
} from "@/components/portal/useActionPlanFeed";
import { formatUsdCents } from "@/components/portal/casesDocket";
import {
  acceptedLine,
  acceptedMeta,
  certClause,
  ifAccepted,
  ticketsWord,
} from "@/components/portal/certSentence";
import { formatUsdExact } from "@/components/portal/exposureAxes";
import { rowDay } from "@/components/portal/shortDay";
import { formatDay } from "../actionPlan";
import { isJudgment, whoCell } from "../openItems";
import type { Step } from "../reopenLeg";

/** Delegators (rule 2): the bodies live in useActionPlanFeed and openItems; judgmentRail.test.tsx pins the 60. */
export { certAgency, FDNY_REVIEW_DAYS, isJudgment };

/** B6: "since" is the docket date only, and never a day after today. */
export const sinceOf = (item: ActionItem, today: string): string | null => {
  const d = item.window.docketed;
  return d && d <= today ? d : null;
};
/** "Sat Jan 31, 2026": formatDay adds the year against any other year, so an empty today always adds it. */
export const withYear = (iso: string) => formatDay(iso, "");

const usd = (v: number) =>
  Number.isInteger(v) ? formatUsdExact(v) : formatUsdCents(v);

/** Leo 10-31 C4: interest a day from the day after Fri Jan 8 (payBy, not endsBy), and once the program is gone. */
export const interestShown = (feed: ActionPlanFeed): boolean =>
  !feed.amnesty || feed.today > feed.amnesty.payBy;

export type JudgmentStep = Step & { tip?: string };

/** C1, C2: a correction-first row leads with the certificate, who files it and by when; Jan 9-10 one step, the
 * server's "pay if accepted". null keeps HEAD's steps. */
export const judgmentSteps = (
  item: ActionItem,
  feed: ActionPlanFeed,
): JudgmentStep[] | null => {
  const { today, amnesty } = feed;
  const at = item.sortBy;
  if (!item.judgmentStep || !at || !amnesty || !amnestyOpen(feed)) return null;
  const f = (iso: string) => rowDay(iso, today);
  const ahead = today <= at;
  const end: JudgmentStep = { t: "Closes: paid", meta: "", st: "wait" };
  const ag = certAgency(item);
  if (!ag)
    return [
      {
        t: `Pay ${ticketsWord(item.groupedCount)} at the amnesty price${item.groupedCount > 1 ? " in one checkout" : ""}`,
        meta: ahead ? `By ${f(at)}` : "Now",
        st: "now",
      },
      end,
    ];
  const { when, why, rest } = certClause(item.judgmentStep, 1, at, feed);
  if (!when) return [{ t: item.action, meta: rest ?? why, st: "now" }, end];
  return [
    {
      t: `File the ${ag} certificate of correction`,
      meta: `${whoCell(item)}, ${when}. ${why}`,
      st: "now",
      ...(item.judgmentStep === JUDGMENT_STEP_FDNY_CERT && !ahead
        ? { tip: "FDNY may refuse a repeat BF35; then pay the full balance." }
        : {}),
    },
    {
      t: "Pay this ticket at the amnesty price",
      meta: acceptedMeta(ag),
      st: "wait",
    },
    end,
  ];
};

export interface JudgmentLines {
  since: string | null;
  /** C3: one price, said with what it settles. */
  price: string | null;
  settles: string | null;
  /** Ruling 16 (DOF terms 2(f)): the pay row's checkout guard and its tickets. */
  guard: string | null;
  tickets: readonly string[];
  /** Ruling 5, through Jan 8: certSentence.acceptedLine. */
  accepted: string[];
  law: string | null;
}

/** The rail's "Judgment owed" section: dates in rows, dollars here (ruling 9); every figure printed once. */
export const judgmentLines = (
  item: ActionItem,
  feed: ActionPlanFeed,
): JudgmentLines => {
  const { today, amnesty } = feed;
  const since = sinceOf(item, today);
  const out: JudgmentLines = {
    since: since && `Judgment since ${withYear(since)}.`,
    price: null,
    settles: null,
    guard: null,
    tickets: [],
    accepted: [],
    law: null,
  };
  const priced = item.money.amnesty?.priceUsd;
  if (!item.judgmentStep || !amnesty || !amnestyOpen(feed) || !priced)
    return out;
  out.law = amnesty.lawLine || null;
  const ag = certAgency(item);
  if (ag) {
    out.price = `Amnesty price ${usd(priced)} for this ticket.`;
    out.accepted = ifAccepted(item.judgmentStep) ? [] : [acceptedLine(ag)];
    return out;
  }
  const n = item.groupedCount;
  out.price = `Pay ${n === 1 ? "" : `${n} `}now for ${usd(priced)}.`;
  out.settles = `The amnesty price settles ${ticketsWord(n)} in place of the balance.`;
  out.guard = `Pay only ${ticketsWord(n)} in ${n === 1 ? "its" : "one"} checkout; any other ticket added is paid in full and gives up its new hearing.`;
  out.tickets = item.recordIds;
  return out;
};

/** Ruling 14: a live-window ticket with an amnesty price never reads "Pay now" (0 on Related). */
export const roomLeftLine = (
  item: ActionItem,
  feed: ActionPlanFeed,
): string | null =>
  feed.judgmentOwed &&
  !item.judgmentOwed &&
  feed.amnesty &&
  amnestyOpen(feed) &&
  (item.money.amnesty?.priced ?? 0) > 0
    ? `${JUDGMENT_CLOCK_WORD[JUDGMENT_STEP_PAY]} ${rowDay(feed.amnesty.endsBy, feed.today)}; paying gives up reopening.`
    : null;
